import { useEffect, useLayoutEffect, useRef } from "react";
import { $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND } from "lexical";
import { BoldIcon, ItalicIcon, PlusIcon, UnderLineIcon } from "vega-icons";

import { LinkNode } from "@lexical/link";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { type TeaserEmailPlaceholderVariable } from "@vega-api/generated/types/teaserEmailPlaceholderVariable";
import { ToolbarStatePlugin } from "@vega-core/components/LexicalEditor/plugins/toolbarStatePlugin";
import {
  ToolbarButton,
  ToolbarSeparator,
} from "@vega-core/components/LexicalEditor/toolbar/toolbarButton";
import { useToolbarState } from "@vega-core/components/LexicalEditor/toolbar/toolbarContext";
import {
  $populateFromHtml,
  editorStateToHtml,
} from "@vega-core/components/MinimalRichTextEditor/lexicalHtml";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

const editorTheme = {
  paragraph: "m-0",
  link: "text-text/interactive underline",
  text: {
    bold: "font-bold",
    italic: "italic",
    underline: "underline",
  },
};

const onError = (error: Error) => {
  throw error;
};

type ToolbarProps = {
  placeholderVariables?: TeaserEmailPlaceholderVariable[];
};

function Toolbar({ placeholderVariables }: ToolbarProps) {
  const [editor] = useLexicalComposerContext();
  const { isBold, isItalic, isUnderline } = useToolbarState();

  const insertToken = (token: string) =>
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) selection.insertText(token);
    });

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border/primary px-1 py-0.5">
      <ToolbarButton
        className="focus-outline-themed"
        icon={<BoldIcon className="size-4" />}
        isActive={isBold}
        label={m.editor_bold()}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
      />
      <ToolbarButton
        className="focus-outline-themed"
        icon={<ItalicIcon className="size-4" />}
        isActive={isItalic}
        label={m.editor_italic()}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
      />
      <ToolbarButton
        className="focus-outline-themed"
        icon={<UnderLineIcon className="size-4" />}
        isActive={isUnderline}
        label={m.editor_underline()}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}
      />
      {placeholderVariables && placeholderVariables.length > 0 ? (
        <>
          <ToolbarSeparator />
          {placeholderVariables.map((variable) => (
            <Tooltip
              content={variable.description}
              key={variable.token}
              providerProps={{ delayDuration: 300 }}
            >
              <button
                aria-label={m.coInvestments_emailTemplate_insertPlaceholder({
                  name: variable.description,
                })}
                className="focus-outline-themed body-small-medium flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-border/primary px-2 py-0.5 text-text/secondary transition-colors hover:border-brand-default hover:bg-background/component/hover hover:text-text/primary"
                onMouseDown={(event) => {
                  event.preventDefault();
                  insertToken(variable.token);
                }}
                type="button"
              >
                <PlusIcon className="size-3" />
                {variable.name}
              </button>
            </Tooltip>
          ))}
        </>
      ) : null}
    </div>
  );
}

const HYDRATE_TAG = "hydrate";

type EditorBridgeProps = {
  value: string;
  onChange: (value: string) => void;
  readOnly: boolean;
};

const EditorBridge = ({ value, onChange, readOnly }: EditorBridgeProps) => {
  const [editor] = useLexicalComposerContext();
  const syncedValue = useRef<string | null>(null);

  useEffect(() => {
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  useLayoutEffect(() => {
    if (value === syncedValue.current) return;

    syncedValue.current = value;
    editor.update(() => $populateFromHtml(editor, value), { tag: HYDRATE_TAG });
  }, [editor, value]);

  return (
    <OnChangePlugin
      ignoreSelectionChange
      onChange={(_editorState, _editor, tags) => {
        if (tags.has(HYDRATE_TAG)) return;

        const html = editorStateToHtml(editor);

        if (html === syncedValue.current) return;

        syncedValue.current = html;
        onChange(html);
      }}
    />
  );
};

export type DraftEmailEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hasError?: boolean;
  readOnly?: boolean;
  dataTestId?: string;
  placeholderVariables?: TeaserEmailPlaceholderVariable[];
};

export function DraftEmailEditor({
  value,
  onChange,
  placeholder,
  hasError = false,
  readOnly = false,
  dataTestId,
  placeholderVariables,
}: DraftEmailEditorProps) {
  const initialConfig = {
    namespace: "DraftEmailEditor",
    theme: editorTheme,
    nodes: [LinkNode],
    onError,
    editable: !readOnly,
  };

  return (
    <div
      className={cnTwMerge(
        "rounded-companyBorderRadius border bg-background/primary transition",
        readOnly
          ? "cursor-not-allowed bg-background/component/10"
          : "focus-within:border-brand-default hover:border-brand-default",
        hasError ? "border-text/negative" : "border-border/primary",
      )}
    >
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarStatePlugin>
          {readOnly ? null : (
            <Toolbar placeholderVariables={placeholderVariables} />
          )}
          <div className="relative">
            <RichTextPlugin
              ErrorBoundary={LexicalErrorBoundary}
              contentEditable={
                <ContentEditable
                  aria-label={placeholder}
                  className="min-h-40 w-full px-3 py-2 text-sm text-text/primary outline-none"
                  data-testid={dataTestId}
                />
              }
              placeholder={
                <span className="pointer-events-none absolute left-3 top-2 text-sm text-text/disabled">
                  {placeholder}
                </span>
              }
            />
          </div>
        </ToolbarStatePlugin>
        <HistoryPlugin />
        <LinkPlugin />
        <EditorBridge onChange={onChange} readOnly={readOnly} value={value} />
      </LexicalComposer>
    </div>
  );
}
